import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { extractSectionData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import {
  LibraryReviewHeader,
  VersionComparisonCard
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ContentComparisonCard';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useKeywordsDisplayStore, useEditingFieldsStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useLibraryReview } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useLibraryReview';
import { ContentGenComparisonView } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ContentGenComparisonView';
import { ActionBarProps } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/LibraryReviewActionBar';
import { useEditGeneratedContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useEditGeneratedContent';
import {
  ContentFormSchema,
  ContentForm,
  sectionsToFormValues
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/types/contentFormTypes';

const LibraryReview = () => {
  const { setShowKeywords } = useKeywordsDisplayStore();
  const { clearAllEditing, editingFields } = useEditingFieldsStore();

  // Use custom hook for all logic
  const {
    leftSections,
    rightSections,
    isLoadingLibrary,
    leftMetrics,
    rightMetrics,
    hasRightContent,
    availableVersions,
    leftComparisonOptions,
    leftSelectedVersion,
    rightSelectedVersion,
    leftVersionName,
    rightVersionName,
    leftVersionDetails,
    rightVersionDetails,
    libraryReviewQueryKey,
    isLoadingCompare,
    isLoadingLeft,
    isLoadingRight,
    metricLoadingStates,
    isSaving,
    isExporting,
    error,
    handleBack,
    handleUpdateContentLibrary,
    handleExport,
    handleLeftVersionChange,
    handleRightVersionChange
  } = useLibraryReview();

  const { isPending: isEditingContent } = useEditGeneratedContent();

  useEffect(() => {
    setShowKeywords(true);
  }, [setShowKeywords]);

  // Clear all editing states when component unmounts
  useEffect(() => {
    return () => {
      clearAllEditing();
    };
  }, [clearAllEditing]);

  const leftContentPhrasesKeywords = useMemo(() => 
    leftSections
      ? extractSectionData({ weightedKeywordPhrases: { sections: leftSections } })
      : {}
  , [leftSections]);

  const rightContentPhrasesKeywords = useMemo(() => 
    rightSections
      ? extractSectionData({ weightedKeywordPhrases: { sections: rightSections } })
      : {}
  , [rightSections]);

  // Initialize form with content data
  const formDefaultValues = useMemo<ContentForm>(() => {
    const currentContent = sectionsToFormValues(leftContentPhrasesKeywords);
    const generatedContent = sectionsToFormValues(rightContentPhrasesKeywords);

    return {
      currentContent: {
        title: currentContent.title || '',
        bullets: currentContent.bullets || [],
        description: currentContent.description || ''
      },
      generatedContent: {
        title: generatedContent.title || '',
        bullets: generatedContent.bullets || [],
        description: generatedContent.description || ''
      }
    };
  }, [leftContentPhrasesKeywords, rightContentPhrasesKeywords]);

  const methods = useForm<ContentForm>({
    mode: 'onChange',
    resolver: zodResolver(ContentFormSchema),
    defaultValues: formDefaultValues
  });

  // Update form when content changes, but only if no fields are currently being edited
  useEffect(() => {
    if (!isLoadingLeft && !isLoadingRight && editingFields.size === 0) {
      methods.reset(formDefaultValues);
    }
  }, [formDefaultValues, isLoadingLeft, isLoadingRight, methods, editingFields]);

  if (error) {
    const errorMessage = error?.message || 'Error loading content.';
    return (
      <BasePageContainer>
        <div>Error: {errorMessage}</div>
      </BasePageContainer>
    );
  }

  const getSaveMenuOptions = () => {
    return [
      {
        id: 'saveToLibrary',
        label: 'Save to library'
      },
      {
        id: 'saveToMaster',
        label: 'Save as master'
      }
    ];
  };

  const handleSaveOption = (option: { id: string; label: string }) => {
    if (option.id === 'saveToLibrary') {
      handleUpdateContentLibrary('draft');
    } else if (option.id === 'saveToMaster') {
      handleUpdateContentLibrary('master');
    }
  };

  const getActionBarProps = (): ActionBarProps => ({
    onBack: handleBack,
    onNext: undefined,
    onArchive: hasRightContent ? () => handleUpdateContentLibrary('archive') : undefined,
    // Inline editing already saves changes via EditableContentCard, so just call handleSaveOption directly
    onSave: hasRightContent ? handleSaveOption : undefined,
    hasGeneratedContent: hasRightContent,
    canGoNext: false,
    canGoBack: true,
    isSaving: isSaving || isEditingContent,
    getSaveMenuOptions
  });

  return (
    <FormProvider {...methods}>
      <BasePageContainer>
        <LibraryReviewHeader handleExport={handleExport} isExporting={isExporting} />
        <Flex gap={41} sx={{ mb: '32px' }}>
          <VersionComparisonCard
            versionName={leftVersionName}
            details={leftVersionDetails}
            detailsLoading={isLoadingCompare}
            detailsVariant={leftSelectedVersion === 'current' ? 'current' : leftSelectedVersion === 'master' ? 'master' : 'generated'}
            metrics={{
              contentUtilization: leftMetrics.contentUtilization,
              keywordsUsed: leftMetrics.keywordsUsed as number,
              productiveKeywords: leftMetrics.productiveKeywords as number,
              keywordPopularity: leftMetrics.keywordPopularity
            }}
            isLoading={isLoadingLeft}
            isVersionsLoading={isLoadingLibrary}
            versions={leftComparisonOptions}
            onVersionChange={handleLeftVersionChange}
            onSetAsMaster={() => handleUpdateContentLibrary('master')}
            metricLoadingStates={{
              utilization: metricLoadingStates.leftUtilization || isLoadingLeft || isLoadingRight,
              keywordUsage: metricLoadingStates.leftKeywordUsage || isLoadingLeft || isLoadingRight,
              productivity: metricLoadingStates.leftKeywordProductivity || isLoadingLeft || isLoadingRight,
              popularity: metricLoadingStates.leftKeywordPopularity || isLoadingLeft || isLoadingRight
            }}
          />
          <VersionComparisonCard
            versionName={rightVersionName}
            details={rightVersionDetails}
            detailsLoading={isLoadingCompare}
            detailsVariant={rightSelectedVersion === 'current' ? 'current' : rightSelectedVersion === 'master' ? 'master' : 'generated'}
            metrics={{
              contentUtilization: rightMetrics.contentUtilization,
              keywordsUsed: rightMetrics.keywordsUsed as number,
              productiveKeywords: rightMetrics.productiveKeywords as number,
              keywordPopularity: rightMetrics.keywordPopularity
            }}
            changes={rightMetrics.changes}
            isLoading={isLoadingRight}
            isVersionsLoading={isLoadingLibrary}
            versions={availableVersions}
            onVersionChange={handleRightVersionChange}
            onSetAsMaster={() => handleUpdateContentLibrary('master')}
            metricLoadingStates={{
              utilization: metricLoadingStates.rightUtilization || isLoadingLeft || isLoadingRight,
              keywordUsage: metricLoadingStates.rightKeywordUsage || isLoadingLeft || isLoadingRight,
              productivity: metricLoadingStates.rightKeywordProductivity || isLoadingLeft || isLoadingRight,
              popularity: metricLoadingStates.rightKeywordPopularity || isLoadingLeft || isLoadingRight
            }}
          />
        </Flex>

        {/* Content comparison - handles its own loading state per side */}
        <ContentGenComparisonView
          currentContentPhrasesKeywords={leftContentPhrasesKeywords}
          generatedContentPhrasesKeywords={rightContentPhrasesKeywords}
          actionBarProps={getActionBarProps()}
          queryKey={libraryReviewQueryKey}
          leftLoading={isLoadingLeft}
          rightLoading={isLoadingRight}
        />
      </BasePageContainer>
    </FormProvider>
  );
};

export default LibraryReview;
